/**
 * Tipos de vista de la API HTTP. REGLA DE ORO #1: apps/web solo importa
 * @voicecheck/contracts y habla con la API por HTTP. Los DTOs que el contrato
 * aún no expone se declaran AQUÍ como espejo del JSON que devuelve /v1/*,
 * nunca importando paquetes de dominio. Si el contrato crece, estos tipos
 * migran a @voicecheck/contracts.
 */
import type { Rol, TipoEvaluacion, EstadoSesion, EstadoBateria } from "@voicecheck/contracts";

export type { Rol, TipoEvaluacion, EstadoSesion } from "@voicecheck/contracts";

// ---- assessment v2 (batería PEFF) -----------------------------------------
// El contrato ya expone el modelo v2 completo; lo re-exportamos para que las
// pantallas importen de un único sitio (`src/api/types.ts`).
export type {
  AdministrationOrder,
  BandaEtaria,
  BateriaCompletaDTO,
  BateriaDTO,
  CodigoEjercicio,
  EjercicioDTO,
  ElicitationMode,
  EstadoBateria,
  EstadoEjercicio,
  EstadoEvaluacion,
  EvaluacionDTO,
  EvaluacionPeff,
  Exigibilidad,
  ItemDTO,
  PeffReportDTO,
} from "@voicecheck/contracts";

/** Motivo de suspensión aceptado por /v1/assessment/baterias/:id/suspend. */
export type MotivoSuspension = "SENAL_ADULTO" | "SIN_INTENTO";

/** Flags de administración de un ítem (PATCH /v1/assessment/items/:id). */
export interface RegistroItem {
  audioRef?: string;
  fonemasProducidos?: string[];
  withHint?: boolean;
  noEvoca?: boolean;
  revisarAudio?: boolean;
  sinIntento?: boolean;
  dudoso?: boolean;
}

/** Espejo de PeffApoyo (word-bank): apoyos + medios de estímulo por ítem del plan.
 *  Texto/URLs son `null` cuando no hay contenido activo; el player degrada
 *  (imagen→texto, audio ausente→sin botón, SIN TTS de relleno). */
export interface PeffApoyo {
  wordId: string;
  pistaSemantica: string | null;
  preguntaDistractora: string | null;
  /** URL firmada de la imagen de estímulo (denominación); null si no hay. */
  imagenUrl: string | null;
  /** URL firmada del audio modelo humano; null si no hay. */
  audioModeloUrl: string | null;
}

/** Espejo del resumen de batería (GET /v1/assessment/baterias?childId=).
 *  Lista compacta para el historial del padre; `EstadoBateria` viene del
 *  contrato (re-exportado arriba). */
export interface BateriaResumen {
  id: string;
  childId: string;
  banda: "A" | "B" | "C" | "D";
  estado: EstadoBateria;
  createdAt: string;
  procesadoEn: string | null;
  profile: string | null;
  ejerciciosTotal: number;
  ejerciciosCompletados: number;
  /** Batería de PRUEBA (QA): el historial la marca con una etiqueta distinta. */
  modoPrueba: boolean;
}

/** Espejo de la fila de la vista GLOBAL de Evaluaciones del terapeuta
 *  (GET /v1/assessment/baterias/global). Cruza propios + vinculados (filtra la
 *  RLS). El nombre del niño y la firma M11 se hidratan en bloque en el cliente. */
export interface BateriaGlobalResumen {
  id: string;
  childId: string;
  edadMeses: number;
  banda: "A" | "B" | "C" | "D";
  estado: EstadoBateria;
  modoPrueba: boolean;
  profile: string | null;
  createdAt: string;
  procesadoEn: string | null;
  flagsRevisarAudio: number;
  flagsDudoso: number;
}
export interface ListaBateriasGlobal {
  items: BateriaGlobalResumen[];
  total: number;
}
/** Contadores accionables de la cabecera de Evaluaciones (scoped por RLS). */
export interface BateriaGlobalStats {
  porProcesar: number;
  conMarcas: number;
  procesadasRecientes: number;
}
/** Filtros de la vista global (GET /v1/assessment/baterias/global). */
export interface FiltrosBateriaGlobal {
  childId?: string;
  estado?: EstadoBateria;
  banda?: "A" | "B" | "C" | "D";
  modo?: "prueba" | "normal";
  conFlags?: boolean;
  desde?: string;
  hasta?: string;
  limit?: number;
  offset?: number;
}

// ---- clinical-review (M11) ------------------------------------------------
// Espejo de la mesa de revisión clínica: el terapeuta re-examina, corrige por
// ítem, valida y FIRMA. El contrato aún no expone estos DTOs (fase clínica): se
// declaran aquí como espejo del JSON de /v1/clinical-review/*.
export interface CorreccionItem {
  id: string;
  bateriaId: string;
  itemId: string;
  itemRef: string;
  codigoOriginal: string | null;
  codigoCorregido: string | null;
  transcripcionCorregida: string | null;
  comentario: string | null;
  terapeutaId: string;
  createdAt: string;
}
export interface CorregirItemInput {
  codigoOriginal?: string;
  codigoCorregido?: string;
  transcripcionCorregida?: string;
  comentario?: string;
}
export interface ItemRevisionFlags {
  withHint: boolean;
  noEvoca: boolean;
  revisarAudio: boolean;
  dudoso: boolean;
  pendienteValidacionClinica: boolean;
}
export interface ItemRevision {
  itemId: string;
  evaluacion: "FONETICA" | "FONOLOGICA";
  ejercicioCodigo: string;
  orden: number;
  estimulo: string;
  phoneme: string;
  targetPhonemes: string[];
  fonemasProducidos: string[];
  codigosMotor: string[];
  estados: string[];
  flags: ItemRevisionFlags;
  audioUrl: string | null;
  correccion: CorreccionItem | null;
}
export interface Validacion {
  id: string;
  bateriaId: string;
  terapeutaId: string;
  observaciones: string | null;
  conclusionProfesional: string | null;
  firmadaEn: string | null;
  nombreProfesional: string | null;
  numeroColegiatura: string | null;
  pais: string | null;
  createdAt: string;
  updatedAt: string;
}
/** Espejo del estado de firma M11 por batería (POST /v1/clinical-review/
 *  validaciones/estado). Solo llegan las baterías con validación; ausente = sin
 *  firma. Hidrata la vista global sin N+1. */
export interface EstadoValidacion {
  bateriaId: string;
  firmada: boolean;
  firmadaEn: string | null;
}
export interface Workbench {
  bateriaId: string;
  childId: string;
  estado: string;
  /** Batería de PRUEBA (QA): la mesa muestra el banner "SIN VALIDEZ CLÍNICA". */
  modoPrueba: boolean;
  profile: string | null;
  engineVersion: string | null;
  inventoryVersion: string | null;
  scoringVersion: string | null;
  items: ItemRevision[];
  validacion: Validacion | null;
}
export interface Recomendacion {
  nivel: "consultar" | "vigilar" | "tranquilizador" | "no_concluyente";
  titulo: string;
  mensaje: string;
}
export interface InformeValidadoPor {
  nombreProfesional: string;
  numeroColegiatura: string;
  firmadaEn: string;
}
export interface Informe {
  bateriaId: string;
  childId: string;
  variante: "padre" | "terapeuta";
  estadoInforme: "pre_informe" | "validado";
  /** Batería de PRUEBA (QA): banner imborrable "EVALUACIÓN DE PRUEBA — SIN
   *  VALIDEZ CLÍNICA" en ambas variantes (padre y terapeuta). */
  modoPrueba: boolean;
  profile: string | null;
  engineVersion: string | null;
  inventoryVersion: string | null;
  scoringVersion: string | null;
  recomendacion: Recomendacion;
  disclaimer: string;
  ctaMarketplace: string | null;
  validadoPor: InformeValidadoPor | null;
  observaciones: string | null;
  conclusionProfesional: string | null;
  correcciones: CorreccionItem[];
  preInformePrevioDisponible: boolean;
}

/** Espejo de una invitación a revisar (POST /v1/identity/shares). El padre la
 *  crea desde el marketplace; el terapeuta la acepta (→ Vinculo) o rechaza. */
export interface Compartida {
  id: string;
  bateriaId: string;
  childId: string;
  padreId: string;
  terapeutaId: string;
  estado: "pendiente" | "aceptada" | "rechazada";
  linkId: string | null;
  createdAt: string;
  resueltaEn: string | null;
}

// ---- identity (M4) --------------------------------------------------------
export interface Usuario {
  id: string;
  email: string;
  nombre: string;
  rol: Rol;
  createdAt: string;
}
export interface Tokens {
  accessToken: string;
  refreshToken: string;
  accessExpiraEn: string;
  refreshExpiraEn: string;
}
export interface SesionAuth {
  usuario: Usuario;
  tokens: Tokens;
}
export interface Nino {
  id: string;
  nombre: string;
  fechaNacimiento: string;
  /** null si es un paciente creado por un terapeuta (aún sin padre). */
  padreId: string | null;
  createdBy: string | null;
  sexo: "M" | "F" | "X" | null;
  notas: string | null;
  terapeutaIds: string[];
}
export type EstadoVinculo = "pendiente_de_vinculacion" | "activa" | "revocada";
export interface Vinculo {
  id: string;
  childId: string;
  terapeutaId: string;
  estado: EstadoVinculo;
  iniciadoPor: "padre" | "terapeuta";
  createdAt: string;
  activadaEn: string | null;
  revocadaEn: string | null;
}
export type TipoConsentimiento = "audio" | "fotos_orofaciales";
export interface EstadoConsentimientos {
  audio: boolean;
  fotosOrofaciales: boolean;
  /** Fecha ISO en que se declaró/otorgó el consentimiento de audio; null si no hay. */
  audioDeclaradoEn: string | null;
}
export interface CodigoVinculo {
  codigo: string;
  expiraEn: string;
}
export interface InvitacionNino {
  token: string;
  expiraEn: string;
}

// ---- panel admin (herramienta interna) ------------------------------------
// Espejo del JSON de /v1/*/admin/*. El admin SOLO LEE datos clínicos (la edición
// clínica es del terapeuta vía clinical-review). El contrato aún no los expone.
export interface UsuarioAdmin extends Usuario {
  /** Cuenta activa; si es false no puede iniciar sesión (baja reversible). */
  activo: boolean;
}
export interface AdminStats {
  usuarios: { padre: number; terapeuta: number; admin: number; total: number; activos: number };
  ninos: number;
  vinculosActivos: number;
}
export interface TerapeutaVinculado {
  id: string;
  nombre: string;
  email: string;
}
export interface NinoAdmin {
  id: string;
  nombre: string;
  fechaNacimiento: string;
  padre: { id: string; nombre: string; email: string } | null;
  terapeutas: TerapeutaVinculado[];
}
/** Fila del listado GLOBAL de baterías (cruza padres). El nombre del niño/padre
 *  se cruza en el cliente con el listado de niños (viven en identity). */
export interface BateriaAdminResumen {
  id: string;
  childId: string;
  edadMeses: number;
  banda: "A" | "B" | "C" | "D";
  estado: EstadoBateria;
  modoPrueba: boolean;
  profile: string | null;
  engineVersion: string | null;
  scoringVersion: string | null;
  createdBy: string;
  createdAt: string;
  procesadoEn: string | null;
  ejerciciosTotal: number;
  ejerciciosCompletados: number;
}
export interface ListaBaterias {
  items: BateriaAdminResumen[];
  total: number;
}
export interface BateriaAdminStats {
  total: number;
  qa: number;
  porEstado: Record<string, number>;
}
export interface PeffCatalogStats {
  silabas: number;
  palabrasFoneticas: number;
  oraciones: number;
  corpusPsf: number;
  total: number;
}
// ---- Banco de Palabras (corpus PEFF, SOLO LECTURA) ----
// Espejo de /v1/word-bank/admin/word-bank[/:id]. El corpus alimenta el motor de
// scoring (regla de oro #3): el panel lo navega, no lo edita.
export type TipoBateriaCorpus = "fonologica" | "fonetica";
export type EstadoCorpus = "activo" | "revision";
export interface CorpusItemResumen {
  id: string; // "psf:<uuid>" | "fon:<uuid>" | "sil:<uuid>" | "ora:<uuid>"
  tipoBateria: TipoBateriaCorpus;
  /** tabla/ejercicio (espeja word_media.corpus_tipo): psf·fonetica_palabra·silaba·oracion. */
  corpusTipo: CorpusTipoMedia;
  palabra: string;
  procesoPrincipal: string | null;
  posicion: string | null;
  edadExigencia: number | null;
  banda: string | null;
  estado: EstadoCorpus;
  dialecto: string;
  /** cobertura de medios activos (tabla sidecar word_media). */
  tieneImagen: boolean;
  tieneAudio: boolean;
}
export interface CorpusItemDetalle extends CorpusItemResumen {
  orden: number | null;
  procesosSecundarios: string[];
  titular2: string | null;
  alternativasRotacion: string[];
  estructuraSilabica: string | null;
  pistaSemantica: string | null;
  preguntaDistractora: string | null;
  imagenUrl: string | null;
  notasClinicas: string | null;
  pendienteValidacionClinica: boolean;
}
export interface ListaCorpus {
  items: CorpusItemResumen[];
  total: number;
}
// ---- Medios de estímulo (word_media sidecar) ----
export type CorpusTipoMedia = "psf" | "fonetica_palabra" | "silaba" | "oracion";
export type MediaTipo = "imagen" | "audio";
export type EstadoMedia = "activo" | "reemplazado" | "eliminado";
export interface WordMediaItem {
  id: string;
  corpusTipo: CorpusTipoMedia;
  corpusRef: string;
  mediaTipo: MediaTipo;
  mimeType: string;
  tamanoBytes: number;
  duracionMs: number | null;
  estado: EstadoMedia;
  version: number;
  subidoPor: string | null;
  subidoPorNombre: string | null;
  createdAt: string;
  url: string;
}
export interface MediosDeItem {
  imagen: WordMediaItem | null;
  audio: WordMediaItem | null;
  historial: WordMediaItem[];
}
// ---- Licencias (gestión admin sobre M10 real) ----
// Espejo de /v1/billing/admin/licenses[/:id]. Opera sobre licenses/subscriptions
// reales; la activación sigue siendo webhook-driven (regla de oro M10).
export type EstadoLicencia = "activa" | "inactiva";
// EstadoSuscripcion ya está declarado en el bloque licensing-billing (M10) más abajo.
export interface PaymentEventDTO {
  id: string;
  eventId: string;
  gateway: string;
  tipo: string;
  gatewayRef: string | null;
  resultado: string | null;
  procesadoEn: string | null;
  createdAt: string;
}
export interface LicenciaAdminResumen {
  licenseId: string;
  subscriptionId: string;
  terapeutaId: string;
  terapeutaNombre: string | null;
  terapeutaEmail: string | null;
  productoCodigo: string | null;
  productoNombre: string | null;
  estadoLicencia: EstadoLicencia;
  estadoSuscripcion: EstadoSuscripcion;
  currentPeriodEnd: string | null;
  graciaHasta: string | null;
  activadaEn: string | null;
  porVencer: boolean;
}
export interface LicenciaAdminDetalle extends LicenciaAdminResumen {
  desactivadaEn: string | null;
  gateway: string;
  gatewayRef: string | null;
  productoIntervalo: string | null;
  productoPrecio: number | null;
  productoMoneda: string | null;
  subCreatedAt: string;
  historialPagos: PaymentEventDTO[];
  historialOffline: PagoOffline[];
  activadaPorOffline: boolean;
}
export interface ListaLicencias {
  items: LicenciaAdminResumen[];
  total: number;
}
// Espejo de /v1/billing/admin/payments/offline. Pagos fuera de pasarela
// (transferencia/depósito/Yape/Plin/efectivo) con comprobante obligatorio; su
// registro activa la licencia (paridad con el webhook). Ver M10 regla de oro.
export type MetodoPagoOffline =
  | "transferencia"
  | "deposito"
  | "yape"
  | "plin"
  | "efectivo"
  | "otro";
export type EstadoPagoOffline = "registrado" | "anulado";
export const METODOS_PAGO_OFFLINE: MetodoPagoOffline[] = [
  "transferencia",
  "deposito",
  "yape",
  "plin",
  "efectivo",
  "otro",
];
export interface PagoOffline {
  id: string;
  subscriptionId: string;
  terapeutaId: string;
  terapeutaNombre: string | null;
  terapeutaEmail: string | null;
  productId: string;
  productoCodigo: string | null;
  productoNombre: string | null;
  monto: number;
  moneda: string;
  metodo: MetodoPagoOffline;
  referenciaOperacion: string | null;
  fechaPago: string;
  motivoDiferencia: string | null;
  estado: EstadoPagoOffline;
  registradoPor: string | null;
  registradoPorNombre: string | null;
  anuladoPor: string | null;
  anuladoMotivo: string | null;
  anuladoEn: string | null;
  createdAt: string;
  comprobanteUrl: string | null;
}
export interface ListaPagosOffline {
  items: PagoOffline[];
  total: number;
}
// ---- Configuración global (panel admin) ----
// Espejo de /v1/config/admin/config. `editable` se persiste; `motor` e
// `integraciones` son SOLO LECTURA (constantes de código / estado de entorno).
export type AdministrationOrderDefault = "DENOMINACION_PRIMERO" | "REPETICION_PRIMERO";
export interface ConfigUltimoCambio {
  por: string | null;
  porNombre: string | null;
  en: string;
}
export interface ConfigEditable {
  dialectosHabilitados: string[];
  notificaciones: Record<string, boolean>;
  administrationOrderDefault: AdministrationOrderDefault;
  ultimoCambio: ConfigUltimoCambio | null;
}
export interface ConfigMotor {
  scoringVersion: string;
  engineVersion: string;
  inventoryVersion: string;
  peffInventoryVersion: string;
  sufficiencyGate: string;
  administrationOrderOptions: AdministrationOrderDefault[];
}
export interface ConfigIntegracion {
  clave: string;
  nombre: string;
  conectado: boolean;
}
export interface ConfigAdmin {
  editable: ConfigEditable;
  motor: ConfigMotor;
  integraciones: ConfigIntegracion[];
}
export interface ActualizarConfigInput {
  dialectosHabilitados?: string[];
  notificaciones?: Record<string, boolean>;
  administrationOrderDefault?: AdministrationOrderDefault;
}
export interface LicenciasStats {
  licenciasActivas: number;
}

// ---- word-bank (M3) -------------------------------------------------------
export type Posicion = "ISPP" | "ISIP" | "CSIP" | "CSFP";
export type TipoPrueba = "fonologica" | "simplificada";
export type PropiedadPrueba = "sistema" | "personalizada";
export interface MediaRefs {
  imagenRef: string | null;
  audioModeloRef: string | null;
  videoModeloRef: string | null;
}
export interface Palabra {
  id: string;
  texto: string;
  fonemaObjetivo: string | null;
  posicion: Posicion;
  estructuraSilabica: string;
  edadMinima: number;
  segmento: string;
  media: MediaRefs;
}
export interface Prueba {
  id: string;
  nombre: string;
  tipo: TipoPrueba;
  propiedad: PropiedadPrueba;
  terapeutaId: string | null;
  descripcion: string | null;
  createdAt: string;
}
export interface PruebaConItems extends Prueba {
  palabras: Palabra[];
}

// ---- assessment (M5) ------------------------------------------------------
export interface Sesion {
  id: string;
  childId: string;
  tipo: TipoEvaluacion;
  estado: EstadoSesion;
  createdBy: string;
  engineVersion: string | null;
  inventoryVersion: string | null;
  createdAt: string;
  procesadoEn: string | null;
}
export interface PalabraSesion {
  id: string;
  sesionId: string;
  orden: number;
  palabraObjetivo: string;
  fonemasObjetivo: string[];
  audioRef: string | null;
  wordId: string | null;
}
export interface DeteccionResultado {
  codigo: string;
  detalle: string;
  confianza: number;
}
export interface ResultadoPalabra {
  id: string;
  sesionId: string;
  palabraSesionId: string;
  palabraObjetivo: string;
  fonemasObjetivo: string[];
  fonemasProducidos: string[];
  correcta: boolean;
  detecciones: DeteccionResultado[];
  confianzaVias: number | null;
  revisionHumana: boolean;
  engineVersion: string;
  inventoryVersion: string;
}
export interface SesionConResultados extends Sesion {
  palabras: PalabraSesion[];
  resultados: ResultadoPalabra[];
}

// ---- media (M8) -----------------------------------------------------------
export const TIPOS_FOTO = [
  "labios_reposo",
  "sonrisa",
  "boca_abierta",
  "lengua_protruida",
  "lengua_elevada",
  "perfil",
] as const;
export type TipoFoto = (typeof TIPOS_FOTO)[number];
export interface SubidaAudio {
  audioRef: string;
  urlSubida: string;
  /** Cabeceras firmadas que el PUT debe reproducir EXACTAS (hoy solo content-type;
   *  SSE deshabilitado temporalmente — MinIO prod sin KMS). */
  headers: Record<string, string>;
  expiraEn: string;
}
export interface SubidaFoto {
  fotoRef: string;
  urlSubida: string;
  headers: Record<string, string>;
  expiraEn: string;
}
export interface FotoRegistro {
  childId: string;
  tipo: TipoFoto;
  objectKey: string;
  subidoPor: string;
}

// ---- reports (M7) ---------------------------------------------------------
export type Variante = "padre" | "terapeuta";
export interface DeteccionReporte {
  codigo: string;
  detalle: string;
  confianza: number;
}
export interface ItemPalabra {
  palabraObjetivo: string;
  fonemasObjetivo: string[];
  fonemasProducidos: string[];
  correcta: boolean;
  detecciones: DeteccionReporte[];
  confianzaVias: number | null;
  revisionHumana: boolean;
  audioUrl?: string;
}
export interface AgregadoProceso {
  codigo: string;
  conteo: number;
}
export interface Narrativa {
  resumen: string;
  recomendaciones: string;
}
interface ReporteComun {
  sesionId: string;
  childId: string;
  tipo: TipoEvaluacion;
  estado: EstadoSesion;
  engineVersion: string | null;
  inventoryVersion: string | null;
  totalPalabras: number;
  palabrasCorrectas: number;
  palabrasEnRevision: number;
  porPalabra: ItemPalabra[];
  agregadosPorProceso: AgregadoProceso[];
  narrativa: Narrativa;
}
export interface ReportePadre extends ReporteComun {
  variante: "padre";
  disclaimer: string;
  ctaMarketplace: string;
}
export interface ReporteTerapeuta extends ReporteComun {
  variante: "terapeuta";
}
export type Reporte = ReportePadre | ReporteTerapeuta;

// ---- treatment (M6) -------------------------------------------------------
export type ModeloTipo = "video" | "audio";
export type OrigenPlan = "auto" | "terapeuta";
export type EstadoPlan = "activo" | "archivado";
export type EstadoPractica = "en_progreso" | "completada" | "abandonada";
export type ResultadoIntento = "correcto" | "en_progreso";
export interface PlanPalabra {
  id: string;
  grupoId: string;
  orden: number;
  wordId: string | null;
  texto: string;
  posicion: Posicion | null;
}
export interface GrupoFonema {
  id: string;
  planId: string;
  orden: number;
  fonemaObjetivo: string;
  codigos: string[];
  modeloTipo: ModeloTipo | null;
  modeloMediaRef: string | null;
  instruccionRemediacion: string | null;
  palabras: PlanPalabra[];
}
export interface Plan {
  id: string;
  childId: string;
  sourceAssessmentId: string | null;
  origen: OrigenPlan;
  disclaimer: string | null;
  umbralTolerante: number;
  engineVersion: string | null;
  inventoryVersion: string | null;
  estado: EstadoPlan;
  createdBy: string | null;
  createdAt: string;
}
export interface PlanCompleto extends Plan {
  grupos: GrupoFonema[];
}
export interface SesionPractica {
  id: string;
  planId: string;
  childId: string;
  estado: EstadoPractica;
  startedAt: string;
  completedAt: string | null;
  createdBy: string | null;
}
export interface ModeloGrupo {
  grupoId: string;
  fonemaObjetivo: string;
  modeloTipo: ModeloTipo | null;
  modeloMediaRef: string | null;
  instruccionRemediacion: string | null;
}
export interface Remediacion {
  modeloTipo: ModeloTipo | null;
  modeloMediaRef: string | null;
  instruccion: string | null;
}
export interface IntentoResultado {
  planPalabraId: string;
  aceptado: boolean;
  motivo: string;
  per: number;
  intentos: number;
  maxIntentos: number;
  debeAvanzar: boolean;
  resultado: ResultadoIntento;
  remediacion?: Remediacion;
  refuerzo?: string;
  engineVersion: string;
  inventoryVersion: string;
}
export interface ProgresoFonema {
  fonemaObjetivo: string;
  aciertos: number;
  total: number;
  pct: number | null;
}
export interface PuntoSerieProgreso {
  semana: string;
  fonemaObjetivo: string;
  aciertos: number;
  total: number;
  pct: number;
}

// ---- licensing-billing (M10) ----------------------------------------------
export type IntervaloPlan = "mensual" | "anual";
export type Audiencia = "terapeuta" | "padre";
export type Modalidad = "suscripcion" | "paquete";
export type EstadoSuscripcion =
  | "pendiente_pago"
  | "activa"
  | "morosa"
  | "cancelada"
  | "expirada";
export interface Product {
  id: string;
  codigo: string;
  nombre: string;
  descripcion: string | null;
  audiencia: Audiencia;
  modalidad: Modalidad;
  intervalo: IntervaloPlan | null;
  precio: number;
  moneda: string;
  /** Evaluaciones incluidas por período/paquete; null = ilimitado (solo suscripción). */
  evaluacionesIncluidas: number | null;
  /** Vigencia de créditos de paquete en días; null = no expiran. */
  vigenciaDias: number | null;
  activo: boolean;
  createdAt: string;
}
// Espejo de /v1/billing/admin/products. `enUso` (≥1 suscripción de cualquier
// estado O ≥1 pago offline) bloquea precio/moneda/intervalo; cambiar precio =
// nueva versión (duplicar). El `codigo` se auto-genera en backend (no editable).
export interface ProductoAdmin extends Product {
  suscripcionesActivas: number;
  suscripcionesTotal: number;
  pagosOffline: number;
  enUso: boolean;
}
export interface CrearPlanInput {
  nombre: string;
  descripcion?: string;
  audiencia?: Audiencia;
  modalidad?: Modalidad;
  precio: number;
  moneda?: string;
  intervalo?: IntervaloPlan | null;
  evaluacionesIncluidas?: number | null;
  vigenciaDias?: number | null;
  activo?: boolean;
}
export interface EditarPlanInput {
  nombre?: string;
  descripcion?: string | null;
  activo?: boolean;
  precio?: number;
  moneda?: string;
  intervalo?: IntervaloPlan | null;
  evaluacionesIncluidas?: number | null;
  audiencia?: Audiencia;
  modalidad?: Modalidad;
  vigenciaDias?: number | null;
}
export interface DuplicarPlanInput extends EditarPlanInput {
  desactivarOriginal?: boolean;
}
// Espejo de créditos de evaluación (/v1/billing/credits, /admin/credits/*).
export type TipoMovimientoCredito =
  | "otorgamiento"
  | "consumo"
  | "expiracion"
  | "ajuste"
  | "backfill"
  | "asignacion_admin";
export type JustificacionAsignacion =
  | "pago_por_transferencia"
  | "cortesia"
  | "correccion"
  | "promocion"
  | "otro";
// Espejo de /v1/billing/admin/credits/assignments (vista "Asignaciones").
export interface AsignacionCredito {
  id: string;
  usuarioId: string;
  usuarioNombre: string | null;
  usuarioEmail: string | null;
  cantidad: number;
  justificacionTipo: JustificacionAsignacion;
  detalle: string | null;
  comprobanteUrl: string | null;
  asignadoPor: string | null;
  asignadoPorNombre: string | null;
  createdAt: string;
}
export interface ListaAsignaciones {
  items: AsignacionCredito[];
  total: number;
}
export interface SaldoCreditos {
  usuarioId: string;
  saldo: number;
  ilimitado: boolean;
  evaluacionesIncluidas: number | null;
}
export interface MovimientoCredito {
  id: string;
  usuarioId: string;
  delta: number;
  tipo: TipoMovimientoCredito;
  motivo: string | null;
  bateriaId: string | null;
  origenTipo: string | null;
  origenRef: string | null;
  actorId: string | null;
  expiraEn: string | null;
  createdAt: string;
}
export interface LedgerCreditos {
  saldo: SaldoCreditos;
  movimientos: MovimientoCredito[];
  total: number;
}
export interface Subscription {
  id: string;
  terapeutaId: string;
  productId: string;
  estado: EstadoSuscripcion;
  gateway: string;
  gatewayRef: string | null;
  currentPeriodEnd: string | null;
  graciaHasta: string | null;
  createdAt: string;
  updatedAt: string;
}
export interface EstadoSuscripcionTerapeuta {
  subscription: Subscription | null;
  licenciaActiva: boolean;
}
export interface Checkout {
  subscription: Subscription;
  checkoutUrl: string;
}
// Compra de paquete por pago único (padre y terapeuta). Espejo de
// /v1/billing/purchase. La acreditación es webhook-driven; el redirect no acredita.
export interface Purchase {
  id: string;
  usuarioId: string;
  productId: string;
  estado: "pendiente_pago" | "pagada" | "anulada";
  gateway: string;
  gatewayRef: string | null;
  evaluaciones: number | null;
  expiraEn: string | null;
  createdAt: string;
  updatedAt: string;
}
export interface CompraIniciada {
  purchase: Purchase;
  checkoutUrl: string;
}

// ---- marketplace (M9) -----------------------------------------------------
export type EstadoVerificacion = "pendiente" | "verificada" | "rechazada";
export interface PerfilPublico {
  id: string;
  terapeutaId: string;
  nombreProfesional: string;
  pais: string;
  ciudad: string;
  especialidades: string[];
  bio: string | null;
  publicado: boolean;
}
export interface PerfilConContacto extends PerfilPublico {
  contactoEmail: string | null;
  contactoWhatsapp: string | null;
}
export interface PerfilPropio extends PerfilConContacto {
  credencialVerificada: boolean;
  createdAt: string;
  updatedAt: string;
}
export interface Verificacion {
  id: string;
  terapeutaId: string;
  pais: string;
  numeroColegiatura: string;
  documentoRef: string | null;
  estado: EstadoVerificacion;
  motivoRechazo: string | null;
  revisadoPor: string | null;
  revisadoEn: string | null;
  createdAt: string;
}

// ---- error de la API ------------------------------------------------------
export interface ApiErrorBody {
  error: { code: string; message: string };
}
